import { useCallback, useEffect, useRef, useState } from "react";

import { faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { clamp } from "lodash-es";
import {
  Controller,
  FieldArrayWithId,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { useFlows } from "src/api/queries";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { Divider } from "src/base-components/Divider";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Label } from "src/base-components/Label";
import { Switch } from "src/base-components/Switch";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { CaseActionFormEditor } from "src/cases/templates/CaseActionFormEditor";
import { FormToggleAccordion } from "src/cases/templates/common/FormToggleAccordion";
import { InvalidFlowDisplay } from "src/cases/templates/common/InvalidFlowDisplay";
import { TemplateItemInfo } from "src/cases/templates/common/TemplateItemInfo";
import {
  ItemType,
  TemplateItemsList,
  TemplateListItem,
} from "src/cases/templates/common/TemplateItemList";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import {
  CaseTemplateActionFormValue,
  CaseTemplateFormValues,
} from "src/cases/templates/formUtils/types";
import { generateInternalId } from "src/cases/templates/internalIdUtils";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useWorkflowReferences } from "src/cases/templates/useWorkflowReferences";
import { Accordion } from "src/design-system/Accordion";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { FlowAndVersionDropdown } from "src/flowsOverview/FlowAndVersionDropdown";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const CaseActionItem: React.FC<{
  index: number;
  active: boolean;
  onRemove: () => void;
  onSelect: () => void;
  canDelete: boolean;
}> = ({ active, index, onSelect, onRemove, canDelete }) => {
  const displayName = useWatch({
    name: `attributes.case_actions.${index}.display_name`,
  });

  return (
    <TemplateListItem
      active={active}
      disabled={!canDelete}
      displayName={displayName}
      itemType={ItemType.ACTION}
      onRemove={onRemove}
      onSelect={onSelect}
    />
  );
};

const CaseActions: React.FC<{
  fields: FieldArrayWithId<
    CaseTemplateFormValues,
    "attributes.case_actions",
    "_id"
  >[];
  activeActionIndex: number;
  onSelectAction: (index: number) => void;
  onAddAction: () => void;
  onRemoveAction: (index: number) => void;
}> = ({
  onSelectAction,
  fields,
  onAddAction,
  onRemoveAction,
  activeActionIndex,
}) => {
  const hasPermission = useCheckFieldPermission();

  return (
    <div className="w-44 shrink-0">
      {!!fields?.length && (
        <TemplateItemsList>
          {fields.map((field, index) => (
            <CaseActionItem
              key={field._id}
              active={activeActionIndex === index}
              canDelete={
                field._isNewlyAppended ||
                hasPermission("delete", "attributes.case_actions")
              }
              index={index}
              onRemove={() => onRemoveAction(index)}
              onSelect={() => onSelectAction(index)}
            />
          ))}
        </TemplateItemsList>
      )}

      {hasPermission("add", "attributes.case_actions") && (
        <Button
          dataLoc="add-case-action-button"
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={onAddAction}
        >
          Add action
        </Button>
      )}
    </div>
  );
};

const ActionInfoWithKeyWatcher: React.FC<{
  actionIndex: number;
  internalId: string;
}> = ({ actionIndex, internalId }) => {
  const hasPermission = useCheckFieldPermission();
  const { updateActionKey } = useWorkflowReferences();
  const previousKeyRef = useRef<string>();

  const currentKey = useWatch({
    name: `attributes.case_actions.${actionIndex}.key`,
  });

  useEffect(() => {
    const normalizedCurrentKey = currentKey || "";

    if (previousKeyRef.current === undefined) {
      previousKeyRef.current = normalizedCurrentKey;
      return;
    }

    if (previousKeyRef.current !== normalizedCurrentKey && internalId) {
      updateActionKey(internalId, normalizedCurrentKey);
      previousKeyRef.current = normalizedCurrentKey;
    }
  }, [currentKey, updateActionKey, internalId]);

  return (
    <TemplateItemInfo
      descriptionPlaceholder="Write a description of the action for the reviewer"
      itemIndex={actionIndex}
      itemType="action"
      keyDisabled={
        !hasPermission("edit", `attributes.case_actions.${actionIndex}.key`)
      }
    />
  );
};

const AccordionItemFormBuilder: React.FC<{
  actionIndex: number;
  accordionValue: string;
  onEnableAccordionItem: (value: string) => void;
  onDisableAccordionItem: (value: string) => void;
}> = ({
  actionIndex,
  accordionValue,
  onEnableAccordionItem,
  onDisableAccordionItem,
}) => (
  <FormToggleAccordion
    accordionValue={accordionValue}
    itemIndex={actionIndex}
    itemType="action"
    onDisableAccordionItem={onDisableAccordionItem}
    onEnableAccordionItem={onEnableAccordionItem}
  >
    <CaseActionFormEditor key={actionIndex} actionIndex={actionIndex} />
  </FormToggleAccordion>
);

const AccordionItemDecisionFlow: React.FC<{
  actionIndex: number;
  accordionValue: string;
  onEnableAccordionItem: (value: string) => void;
  onDisableAccordionItem: (value: string) => void;
}> = ({
  actionIndex,
  accordionValue,
  onEnableAccordionItem,
  onDisableAccordionItem,
}) => {
  const { workspace } = useWorkspaceContext();
  const form = useFormContext<CaseTemplateFormValues>();

  const { errors } = form.formState;

  const flowValue = useWatch({
    name: `attributes.case_actions.${actionIndex}.flow`,
  });
  const isFormEnabled = Boolean(flowValue);

  const {
    data: flows = [],
    isFullyLoaded,
    isError,
  } = useFlows({
    workspaceId: workspace.id,
    includedTypes: ["case_flow"],
    disabled: !isFormEnabled,
  });

  const isInvalidFlow =
    isFullyLoaded &&
    !isError &&
    !!flowValue?.flow_id &&
    !flows.some((f) => f.id === flowValue.flow_id);

  const onToggleFlow = () => {
    if (isFormEnabled) {
      form.setValue(`attributes.case_actions.${actionIndex}.flow`, null);
      onDisableAccordionItem(accordionValue);
    } else {
      form.setValue(`attributes.case_actions.${actionIndex}.flow`, {
        flow_slug: "",
        flow_id: "",
        flow_version_id: "",
        flow_version_name: "",
      });
      onEnableAccordionItem(accordionValue);
    }
  };

  return (
    <Accordion.Item
      dataLoc="decision-flow-accordion"
      isDisabled={!isFormEnabled}
      rightSlot={
        <Switch
          dataLoc="decision-flow-switch"
          enabled={isFormEnabled}
          onChange={onToggleFlow}
        />
      }
      label="Select the Decision Flow to run when this action is triggered"
      value={accordionValue}
    >
      <Card className="w-full">
        <div className="flex flex-1 flex-col">
          <Label required>Decision Flow and version</Label>
          <Controller
            control={form.control}
            name={`attributes.case_actions.${actionIndex}.flow`}
            render={({ field }) => {
              const isLoading = !isFullyLoaded && !!field.value?.flow_id;

              if ((isInvalidFlow || isLoading) && field.value) {
                return (
                  <InvalidFlowDisplay
                    dataLoc={`action-decision-flow-${actionIndex}`}
                    flowSlug={field.value.flow_slug || field.value.flow_id}
                    loading={isLoading}
                    onClear={() =>
                      field.onChange({
                        flow_slug: "",
                        flow_id: "",
                        flow_version_id: "",
                        flow_version_name: "",
                      })
                    }
                  />
                );
              }

              const dropdown = (
                <FlowAndVersionDropdown
                  disabled={
                    !isFormEnabled || !isFullyLoaded || !flows.length || isError
                  }
                  flows={flows}
                  selected={
                    field.value
                      ? {
                          id: field.value.flow_id,
                          versionId: field.value.flow_version_id,
                          flowSlug: field.value.flow_slug,
                          versionName: field.value.flow_version_name,
                        }
                      : null
                  }
                  onSelect={(value) => {
                    field.onChange(
                      value
                        ? {
                            flow_id: value.id,
                            flow_slug: value.flowSlug,
                            flow_version_name: value.versionName,
                            flow_version_id: value.versionId,
                          }
                        : null,
                    );
                  }}
                />
              );

              return isError ? (
                <ErrorHint>
                  Failed to load Decision Flows. Please try again.
                </ErrorHint>
              ) : (
                <Tooltip
                  body="There is no Decision Flow in your workspace that takes a case as input"
                  disabled={
                    flows.length !== 0 || !isFullyLoaded || !isFormEnabled
                  }
                  placement="top"
                  asChild
                >
                  <div className="w-full">{dropdown}</div>
                </Tooltip>
              );
            }}
          />
          {errors.attributes?.case_actions?.[actionIndex]?.flow && (
            <ErrorHint>
              {errors.attributes.case_actions?.[actionIndex].flow.message}
            </ErrorHint>
          )}
        </div>
      </Card>
    </Accordion.Item>
  );
};

export const CaseActionsConfigurationStep: React.FC = () => {
  const step = getStepById("configure-case-actions");
  const [activeActionIndex, setActiveActionIndex] = useState<number>(0);
  const {
    openModal: openDeleteActionModal,
    isOpen: isDeleteActionModalOpen,
    closeModal: closeDeleteActionModal,
    data: actionToDelete,
  } = useModal<CaseActionWithIndex>();

  const hasPermission = useCheckFieldPermission();
  const { removeAction } = useWorkflowReferences();

  const { fields, append, remove } = useFieldArray<
    CaseTemplateFormValues,
    "attributes.case_actions",
    "_id"
  >({
    name: `attributes.case_actions`,
    keyName: "_id",
  });

  const onAddAction = () => {
    append(
      {
        key: "",
        display_name: "",
        description: "",
        _internalId: generateInternalId(),
        _isNewlyAppended: true,
      },
      { focusName: `attributes.case_actions.${fields.length}.display_name` },
    );

    setActiveActionIndex(fields.length);
  };

  const handleRemoveAction = (actionIndex: number) => {
    const actionToDelete = fields[actionIndex];

    if (actionToDelete._internalId) {
      removeAction(actionToDelete._internalId);
    }

    remove(actionIndex);
    setActiveActionIndex(clamp(actionIndex - 1, 0, fields.length - 1));
  };

  const onDeleteAction = (actionIndex: number) => {
    const action = fields[actionIndex];
    if (action._isNewlyAppended) handleRemoveAction(actionIndex);
    else openDeleteActionModal({ ...action, index: actionIndex });
  };

  return (
    <>
      <FormStepWrapper
        description={step.description}
        docLink={step.docLink}
        title={step.title}
      >
        {fields.length ? (
          <div className="flex gap-x-4">
            <CaseActions
              activeActionIndex={activeActionIndex}
              fields={fields}
              onAddAction={onAddAction}
              onRemoveAction={onDeleteAction}
              onSelectAction={setActiveActionIndex}
            />

            <div>
              <Divider orientation="vertical" variant="dark" />
            </div>

            <div
              className="w-full min-w-0 flex-1"
              data-loc="case-action-editor"
            >
              <CaseActionConfigurationAccordion
                key={`case-action-configuration-${activeActionIndex}`}
                actionIndex={activeActionIndex}
                internalId={fields[activeActionIndex]._internalId}
              />
            </div>
          </div>
        ) : (
          <div className="h-110">
            <EmptyState
              actionButton={{
                label: "Add action",
                icon: faPlus,
                onClick: onAddAction,
                isDisabled: !hasPermission("add", "attributes.case_actions"),
              }}
              dataLoc="empty-case-actions"
              description="Configure actions to be performed during the case investigation process."
              headline="No actions configured yet"
              icon={faSearch}
            />
          </div>
        )}
      </FormStepWrapper>
      <DeleteCaseActionModal
        action={actionToDelete}
        open={isDeleteActionModalOpen}
        onClose={closeDeleteActionModal}
        onRemoveAction={handleRemoveAction}
      />
    </>
  );
};

type CaseActionWithIndex = CaseTemplateActionFormValue & {
  index: number;
};

const DeleteCaseActionModal: React.FC<{
  action?: CaseActionWithIndex;
  open: boolean;
  onClose: () => void;
  onRemoveAction: (index: number) => void;
}> = ({ action, open, onClose, onRemoveAction }) => {
  const onDeleteAction = () => {
    if (action) onRemoveAction(action.index);
  };

  return (
    <TextConfirmationModal
      challengeText={action?.display_name}
      description="Are you sure you want to delete this action? This action is not reversible. Any workflows currently using this action will also be deleted."
      label="Enter action name to confirm"
      open={open}
      placeholder={action?.display_name}
      title={`Delete ${action?.display_name}`}
      onClose={onClose}
      onConfirm={onDeleteAction}
    />
  );
};

const CaseActionConfigurationAccordion: React.FC<{
  actionIndex: number;
  internalId: string;
}> = ({ actionIndex, internalId }) => {
  const { getValues } = useFormContext<CaseTemplateFormValues>();

  const [enabledAccordionItems, setEnabledAccordionItems] = useState<string[]>(
    () => {
      const flow = getValues(`attributes.case_actions.${actionIndex}.flow`);
      const form = getValues(`attributes.case_actions.${actionIndex}.form`);
      return [
        "case-action-info",
        form && "case-action-form-builder",
        flow && "case-action-decision-flow",
      ].filter(Boolean) as string[];
    },
  );

  const onDisableAccordionItem = useCallback((value: string) => {
    setEnabledAccordionItems((prevValues) =>
      prevValues.filter((v) => v !== value),
    );
  }, []);

  const onEnableAccordionItem = useCallback((value: string) => {
    setEnabledAccordionItems((prevValues) => {
      if (!prevValues.includes(value)) {
        return [...prevValues, value];
      }
      return prevValues;
    });
  }, []);

  return (
    <Accordion
      value={enabledAccordionItems}
      onChange={(value) => setEnabledAccordionItems(value)}
    >
      <Accordion.Item
        key={`info-${actionIndex}`}
        dataLoc="case-action-info-accordion"
        label="Define action name and description"
        value="case-action-info"
      >
        <ActionInfoWithKeyWatcher
          actionIndex={actionIndex}
          internalId={internalId}
        />
      </Accordion.Item>
      <AccordionItemFormBuilder
        key={`form-builder-${actionIndex}`}
        accordionValue="case-action-form-builder"
        actionIndex={actionIndex}
        onDisableAccordionItem={onDisableAccordionItem}
        onEnableAccordionItem={onEnableAccordionItem}
      />
      <AccordionItemDecisionFlow
        key={`decision-flow-${actionIndex}`}
        accordionValue="case-action-decision-flow"
        actionIndex={actionIndex}
        onDisableAccordionItem={onDisableAccordionItem}
        onEnableAccordionItem={onEnableAccordionItem}
      />
    </Accordion>
  );
};
